import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { Activity, Search, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';

// Map an audit-log entry to a navigable URL where possible
const linkForLog = (log) => {
  const id = log.resource_id;
  if (!id || id === 'multiple') return null;
  switch (log.resource_type) {
    case 'patient':
    case 'patient_file': return `/patients/${log.details?.patient_id || id}`;
    case 'form':
    case 'form_submission': return log.resource_type === 'form_submission' ? null : `/forms`;
    case 'appointment': return `/appointments`;
    case 'task': return `/tasks`;
    case 'encounter': return `/patients`;
    case 'report': return `/reports`;
    default: return null;
  }
};

const ACTION_COLORS = {
  login: 'bg-[#166534]/10 text-[#166534]',
  logout: 'bg-[#64748B]/10 text-[#64748B]',
  delete: 'bg-[#991B1B]/10 text-[#991B1B]',
  create: 'bg-[#1A56DB]/10 text-[#1A56DB]',
  update: 'bg-[#1A56DB]/10 text-[#1A56DB]',
  view: 'bg-[#0E7490]/10 text-[#0E7490]',
  list: 'bg-[#0E7490]/10 text-[#0E7490]',
};

/**
 * Shared 7-day "Worked Cases" chart with click-to-drill-down drawer.
 *
 * @param {Array}   activity   Array of audit-log entries from /api/auth/activity
 * @param {string}  title      Optional heading (default: "Worked Cases — 7 Days")
 * @param {number}  height     Chart height in px (default 200)
 * @param {string}  testId     Root data-testid
 */
export default function WorkedCasesChart({ activity = [], title = 'Worked Cases — 7 Days', height = 200, testId = 'worked-cases-chart' }) {
  const [selectedDay, setSelectedDay] = useState(null); // { key, label, dateStr }
  const [search, setSearch] = useState('');

  // Build 7-day buckets
  const { timelineData, byDay } = useMemo(() => {
    const days = [];
    const map = {};
    const bucketMap = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      days.push({ key, label, dateStr });
      map[key] = { day: label, dayKey: key, dateStr, actions: 0, phi_access: 0 };
      bucketMap[key] = [];
    }
    activity.forEach((log) => {
      const day = (log.timestamp || '').slice(0, 10);
      if (map[day]) {
        map[day].actions += 1;
        if (log.resource_type === 'patient') map[day].phi_access += 1;
        bucketMap[day].push(log);
      }
    });
    return { timelineData: days.map((d) => map[d.key]), byDay: bucketMap };
  }, [activity]);

  const selectedLogs = selectedDay ? byDay[selectedDay.key] || [] : [];
  const filteredLogs = useMemo(() => {
    if (!search.trim()) return selectedLogs;
    const q = search.toLowerCase();
    return selectedLogs.filter(
      (l) =>
        (l.action || '').toLowerCase().includes(q) ||
        (l.resource_type || '').toLowerCase().includes(q) ||
        (l.resource_id || '').toLowerCase().includes(q) ||
        (l.ip_address || '').toLowerCase().includes(q)
    );
  }, [selectedLogs, search]);

  const handleBarClick = (data) => {
    // Recharts passes the payload directly as the first arg on <Bar onClick>
    const payload = data && data.payload ? data.payload : data;
    if (!payload || !payload.dayKey) return;
    setSelectedDay({ key: payload.dayKey, label: payload.day, dateStr: payload.dateStr });
    setSearch('');
  };

  const closeDrawer = () => {
    setSelectedDay(null);
    setSearch('');
  };

  return (
    <>
      <Card className="careiq-card" data-testid={testId}>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#0E7490]" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[10px] text-[#94A3B8] mb-1">Click a bar to see that day's activity</p>
          <div style={{ width: '100%', height }}>
            <ResponsiveContainer>
              <BarChart
                data={timelineData}
                margin={{ top: 5, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" stroke="#64748B" style={{ fontSize: 11 }} />
                <YAxis stroke="#64748B" style={{ fontSize: 11 }} allowDecimals={false} width={28} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }}
                  labelStyle={{ color: '#0A2342', fontWeight: 600 }}
                  cursor={{ fill: '#0E7490', fillOpacity: 0.08 }}
                />
                <Bar dataKey="actions" name="Actions" fill="#0E7490" radius={[3, 3, 0, 0]} style={{ cursor: 'pointer' }} onClick={handleBarClick} />
                <Bar dataKey="phi_access" name="PHI Access" fill="#1A56DB" radius={[3, 3, 0, 0]} style={{ cursor: 'pointer' }} onClick={handleBarClick} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-3 mt-1 text-[10px] text-[#64748B]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#0E7490]" />Actions</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#1A56DB]" />PHI access</span>
          </div>
          {/* Fallback day chips — easier click target than tiny bars */}
          <div className="mt-2 grid grid-cols-7 gap-1" data-testid="worked-cases-day-chips">
            {timelineData.map((d) => (
              <button
                key={d.dayKey}
                type="button"
                onClick={() => handleBarClick(d)}
                className={`px-1 py-1 rounded text-[10px] transition-colors ${
                  d.actions > 0
                    ? 'bg-[#F0FDFA] hover:bg-[#0E7490] hover:text-white text-[#0A2342] cursor-pointer'
                    : 'bg-[#F8FAFC] text-[#94A3B8] cursor-default'
                }`}
                disabled={d.actions === 0}
                data-testid={`day-chip-${d.dayKey}`}
                title={`${d.dateStr} · ${d.actions} actions`}
              >
                <div className="font-semibold">{d.day}</div>
                <div className="text-[9px] opacity-70">{d.actions}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Drill-down drawer */}
      <Dialog open={!!selectedDay} onOpenChange={(v) => { if (!v) closeDrawer(); }}>
        <DialogContent className="max-w-2xl" data-testid="worked-cases-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span>Activity on {selectedDay?.label}, {selectedDay?.dateStr}</span>
              <span className="text-xs font-normal text-[#64748B]">{selectedLogs.length} event(s)</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {selectedLogs.length > 0 && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-[#94A3B8] absolute left-2 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by action, resource, IP…"
                  className="h-8 pl-7 text-sm"
                  data-testid="worked-cases-drawer-search"
                />
              </div>
            )}

            {filteredLogs.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#94A3B8]">
                {selectedLogs.length === 0 ? 'No activity recorded for this day.' : 'No events match your filter.'}
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                      <th className="py-2 pr-2 font-medium">Time</th>
                      <th className="py-2 pr-2 font-medium">Action</th>
                      <th className="py-2 pr-2 font-medium">Resource</th>
                      <th className="py-2 pr-2 font-medium">IP</th>
                      <th className="py-2 font-medium w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const href = linkForLog(log);
                      return (
                        <tr key={log.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`drawer-row-${log.id}`}>
                          <td className="py-1.5 pr-2 text-[#0A2342] whitespace-nowrap">
                            {log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                          </td>
                          <td className="py-1.5 pr-2">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${ACTION_COLORS[log.action] || 'bg-[#F1F5F9] text-[#64748B]'}`}>
                              {log.action || '—'}
                            </span>
                          </td>
                          <td className="py-1.5 pr-2 text-[#64748B]">
                            {log.resource_type || '—'}
                            {log.resource_id && log.resource_id !== 'multiple' && (
                              <span className="text-[#94A3B8] ml-1">· {String(log.resource_id).slice(-6)}</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 text-[#64748B] font-mono">{log.ip_address || '—'}</td>
                          <td className="py-1.5">
                            {href && (
                              <Link
                                to={href}
                                onClick={closeDrawer}
                                className="text-[#0E7490] hover:text-[#0A2342]"
                                title="Open resource"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </Link>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
